<template>
  <div class="text-center">
    <wallet-form-header
      :title="title"
      :message="message"
    />
    <form-builder
      ref="formBuilder"
      :model="credentials"
      :schema="formSchema"
      dense
      @field-changed="fieldChanged"
    >
      <template #password="{ field, label }">
        <password-field
          ref="password"
          v-model="credentials[field]"
          :label="label"
          :rules="passwordRules"
          validate-on-blur
          outlined
          :hide-details="showPasswordValidationHelper"
          @input="fieldChanged(field, $event)"
          @blur="passwordFieldBlur"
        />
        <validation-helper
          v-if="showPasswordValidationHelper"
          v-model="credentials.password"
          :heading="$t('wallet.form.password.validation-label')"
          :validation-rules="newPasswordValidation"
          :field-is-complete="passwordFieldComplete"
          class="mb-2"
        />
      </template>
      <template #confirmPassword="{ field, label }">
        <password-field
          v-model="credentials[field]"
          :label="label"
          :rules="confirmPasswordRules"
          validate-on-blur
          outlined
        />
      </template>
    </form-builder>
    <tc-v-btn
      id="wallet-form-submit-btn"
      block
      color="primary"
      large
      outlined
      class="text-capitalize mb-10 mt-4"
      :loading="loading"
      :disabled="loading"
      @click="submitForm"
    >
      {{ submitButtonText }}
    </tc-v-btn>
  </div>
</template>
<script>
import { WalletRegisterFormSchema } from '@/components/shared/forms/WalletRegisterFormSchema'
import { WalletLoginFormSchema } from '@/components/shared/forms/WalletLoginFormSchema'
import { PasswordRecoveryFormSchema } from '@/components/shared/forms/PasswordRecoveryFormSchema'
import { PasswordResetFormSchema } from '@/components/shared/forms/PasswordResetFormSchema'
import { WalletFormType } from '@/components/wallet/wallet-form-type'
import { FormBuilder } from '@tangocard/mango'
import PasswordField from '@/components/shared/forms/PasswordField'
import ValidationHelper from '@/components/shared/forms/ValidationHelper'
import WalletFormHeader from '@/components/wallet/WalletFormHeader'
import FormValidationRules from '@/components/shared/forms/FormValidationRules'
export default {
  name: 'WalletForm',
  components: {
    ValidationHelper,
    FormBuilder,
    PasswordField,
    WalletFormHeader
  },
  props: {
    formType: {
      type: String,
      validator: v => {
        return Object.values(WalletFormType).includes(v)
      },
      required: true
    },
    emailAddress: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      credentials: {
        emailAddress: '',
        password: '',
        confirmPassword: ''
      },
      formIsValid: true,
      passwordFieldComplete: false
    }
  },
  computed: {
    newPasswordValidation () {
      return {
        min: {
          label: this.$t('wallet.form.password.min', { length: 8 }),
          rule: FormValidationRules.min(8)
        },
        uppercase: {
          label: this.$t('wallet.form.password.uppercase'),
          rule: FormValidationRules.hasUppercaseCharacter()
        },
        specialCharacter: {
          label: this.$t('wallet.form.password.special'),
          rule: FormValidationRules.hasSpecialCharacter()
        },
        number: {
          label: this.$t('wallet.form.password.number'),
          rule: FormValidationRules.hasNumber()
        }
      }
    },
    newPasswordRules () {
      return Object.values(this.newPasswordValidation).map(r => r.rule)
    },
    existingPasswordRules () {
      return [
        FormValidationRules.required(this.$t('wallet.form.password.required'))
      ]
    },
    confirmPasswordRules () {
      return [
        FormValidationRules.matches(
          this.credentials.password,
          this.$t('wallet.form.confirm-password.matches')
        )
      ]
    },
    formDetails () {
      let title
      let message
      let schema
      let submitLabel
      let passwordRules = this.newPasswordRules

      if (this.isRegisterForm) {
        title = this.$t('wallet.register.title')
        message = this.$t('wallet.register.message')
        schema = WalletRegisterFormSchema
        submitLabel = this.$t('wallet.register.submit-button-label')
      } else if (this.formType === WalletFormType.PASSWORD_RECOVERY) {
        title = this.$t('wallet.recover.title')
        message = this.$t('wallet.recover.message')
        schema = PasswordRecoveryFormSchema
        submitLabel = this.$t('wallet.recover.submit-button-label')
      } else if (this.formType === WalletFormType.PASSWORD_RESET) {
        title = this.$t('wallet.reset.title')
        message = this.$t('wallet.reset.message')
        schema = PasswordResetFormSchema
        submitLabel = this.$t('wallet.reset.submit-button-label')
      } else {
        title = this.$t('wallet.login.title')
        message = this.$t('wallet.login.message')
        schema = WalletLoginFormSchema
        submitLabel = this.$t('wallet.login.submit-button-label')
        passwordRules = this.existingPasswordRules
      }
      return { title, message, schema, submitLabel, passwordRules }
    },
    title () {
      return this.formDetails.title
    },
    message () {
      return this.formDetails.message
    },
    submitButtonText () {
      return this.formDetails.submitLabel
    },
    formSchema () {
      return this.formDetails.schema
    },
    passwordRules () {
      return this.formDetails.passwordRules
    },
    isRegisterForm () {
      return this.formType === WalletFormType.REGISTER
    },
    isResetForm () {
      return this.formType === WalletFormType.PASSWORD_RESET
    },
    showPasswordValidationHelper () {
      return this.isRegisterForm || this.isResetForm
    }
  },
  watch: {
    emailAddress: {
      handler (val) {
        this.credentials.emailAddress = val
      },
      immediate: true
    }
  },
  methods: {
    validateForm () {
      this.formIsValid = this.$refs.formBuilder.validate()
    },
    fieldChanged (field, value) {
      this.credentials[field] = value
    },
    submitForm () {
      this.passwordFieldComplete = true
      this.validateForm()
      if (!this.formIsValid) {
        return
      }
      this.$emit('form-submit', this.credentials)
    },
    passwordFieldBlur () {
      this.passwordFieldComplete = true
    },
    resetForm () {
      this.credentials.emailAddress = ''
      this.credentials.password = ''
      this.credentials.confirmPassword = ''
    }
  }
}
</script>
<style lang="scss" scoped></style>
